import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, useDeferredValue } from "react";

import { calculateModes, getBoltArea, modeDensityPerBand, modeDescription } from './acoustics';
import { DEFAULT_COLORMAP, COLORMAPS, buildLUT, colormapGradientVerticalCSS, hexRgba } from './colormaps';
import { INCH, M_TO_FT, formatFtInShort, formatFtInSymbol } from './units';
import { typeColors, typeLabels, SPEED_OF_SOUND } from './constants';
import { INITIAL_HASH } from './parseHash';
import { createPublicWorker } from "./createWorker";
import ColormapPicker from "./ColormapPicker";
import RoomVizGL from "./RoomVizGL";
// CHUNK_1_HIDDEN: import StructuralModal from "./StructuralModal";

// Phase 1 feature flag — flip to true to test RoomVizGL alongside RoomViz.
// Do not delete RoomViz or isoWorker.js until Phase 3 acceptance criteria pass.
const USE_GL_RENDERER = true;

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4, verticalAlign: 'middle' }}>
      <button
        aria-label="More information"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(v => !v)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--c-text-3)', fontSize: 11, lineHeight: 1,
          width: 14, height: 14, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', borderRadius: '50%', fontFamily: 'serif',
        }}
      >
        ⓘ
      </button>
      {show && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--c-card)', border: '1px solid var(--c-border-2)',
          borderRadius: 6, padding: '8px 10px',
          fontSize: 11, color: 'var(--c-text-2)',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          width: 210, lineHeight: 1.5, zIndex: 200,
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          pointerEvents: 'none', whiteSpace: 'normal',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

function Slider({ label, value, onChange, min, max, step, unit, imperial, onInteractStart, onInteractEnd }) {
  const [draft, setDraft] = useState(null);
  const isDim = unit === 'm';
  const toDisplay = (v) => isDim && imperial ? v * M_TO_FT : v;
  const fromDisplay = (v) => isDim && imperial ? v / M_TO_FT : v;
  const displayUnit = isDim && imperial ? 'ft' : unit;
  const decimals = isDim && imperial ? 2 : unit === 'Hz' ? 0 : 2;
  const numericDisplay = toDisplay(value).toFixed(decimals);
  const commit = (raw) => {
    const n = parseFloat(raw);
    if (isFinite(n)) onChange(Math.max(min, Math.min(max, fromDisplay(n))));
    setDraft(null);
  };
  const sliderStep = isDim && imperial ? INCH : step;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--c-text-2)", flexShrink: 0, marginRight: 8 }}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <input
            key={isDim && imperial ? 'i' : 'm'}
            type="number"
            className="num-input"
            value={draft !== null ? draft : numericDisplay}
            onChange={e => setDraft(e.target.value)}
            onFocus={() => setDraft(numericDisplay)}
            onBlur={e => commit(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(null); e.target.blur(); } }}
            min={toDisplay(min).toFixed(decimals)}
            max={toDisplay(max).toFixed(decimals)}
            aria-label={`${label} value`}
            style={{
              width: 64, textAlign: 'right',
              fontFamily: "'DM Mono', monospace",
              fontSize: 13, fontWeight: 500,
              color: 'var(--c-text)',
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border-2)',
              borderRadius: 4,
              padding: '2px 6px',
              transition: 'border-color 0.15s, outline 0.1s',
            }}
          />
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--c-text-3)', flexShrink: 0, minWidth: 16 }}>
            {displayUnit}
          </span>
        </div>
      </div>
      <input
        type="range" min={min} max={max} step={sliderStep} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onMouseDown={() => onInteractStart?.()}
        onTouchStart={() => onInteractStart?.()}
        onMouseUp={() => onInteractEnd?.()}
        onTouchEnd={() => onInteractEnd?.()}
        onBlur={() => onInteractEnd?.()}
        style={{ width: "100%", accentColor: "#E8C547" }}
      />
    </div>
  );
}

function ModeSpectrum({ modes, modesByType, maxFreq, selectedMode, onSelectMode }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 700, h: 120 });
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) setDims({ w: e.contentRect.width, h: 120 });
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = dims.w * dpr;
    canvas.height = dims.h * dpr;
    canvas.style.width = `${dims.w}px`;
    canvas.style.height = `${dims.h}px`;
    ctx.scale(dpr, dpr);

    const pad = { top: 20, right: 23, bottom: 36, left: 23 };
    const plotW = dims.w - pad.left - pad.right;
    const plotH = dims.h - pad.top - pad.bottom;

    ctx.clearRect(0, 0, dims.w, dims.h);

    // 0 Hz axis tick only
    ctx.strokeStyle = "rgba(138,143,152,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pad.left, pad.top); ctx.lineTo(pad.left, pad.top + plotH); ctx.stroke();

    const typeOrder = ["oblique", "tangential", "axial"];
    for (const type of typeOrder) {
      const typeModes = modesByType ? modesByType[type] : modes.filter(m => m.type === type);
      const alpha = type === "axial" ? 0.9 : type === "tangential" ? 0.55 : 0.7;
      const lineW = type === "axial" ? 2.5 : type === "tangential" ? 1.5 : 1.4;
      for (const m of typeModes) {
        const x = pad.left + (m.freq / maxFreq) * plotW;
        ctx.strokeStyle = typeColors[type];
        ctx.globalAlpha = alpha;
        ctx.lineWidth = lineW;
        ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + plotH); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // Selected mode highlight — re-resolve frequency from current modes so it follows dimension changes
    const liveSelected = selectedMode && modes.find(m => m.nx === selectedMode.nx && m.ny === selectedMode.ny && m.nz === selectedMode.nz);
    if (liveSelected) {
      const sx = pad.left + (liveSelected.freq / maxFreq) * plotW;
      ctx.strokeStyle = "#FFFFFF";
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(sx, pad.top); ctx.lineTo(sx, pad.top + plotH); ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath(); ctx.arc(sx, pad.top + 8, 4, 0, Math.PI * 2); ctx.fill();
    }

    const schroeder = 2000 * Math.sqrt(0.5 / (modes.length > 0 ? modes.length / maxFreq : 1));
    if (schroeder <= maxFreq && schroeder > 0) {
      const sx = pad.left + (schroeder / maxFreq) * plotW;
      ctx.strokeStyle = "#94A3B8";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(sx, pad.top); ctx.lineTo(sx, pad.top + plotH); ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [modes, modesByType, dims, maxFreq, selectedMode]);

  const getClosestMode = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const plotW = dims.w - 43;
    const freqAtMouse = ((mx - 23) / plotW) * maxFreq;
    let closest = null, minDist = Infinity;
    for (const m of modes) {
      const d = Math.abs(m.freq - freqAtMouse);
      if (d < minDist) { minDist = d; closest = m; }
    }
    return (minDist / maxFreq) * plotW < 12 ? closest : null;
  };

  const handleMouseMove = (e) => {
    const closest = getClosestMode(e);
    if (closest) {
      const rect = canvasRef.current.getBoundingClientRect();
      setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, mode: closest });
    } else {
      setTooltip(null);
    }
  };

  const handleClick = (e) => {
    const closest = getClosestMode(e);
    if (closest) onSelectMode(closest);
  };

  const pad = { top: 20, right: 23, bottom: 36, left: 23 };
  const plotH = dims.h - pad.top - pad.bottom;
  const schroederFreq = modes.length > 0 ? 2000 * Math.sqrt(0.5 / (modes.length / maxFreq)) : null;
  const freqTicks = [];
  for (let f = 0; f <= maxFreq; f += 50) freqTicks.push(f);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", cursor: tooltip ? "pointer" : "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onClick={handleClick}
      />
      {/* HTML frequency axis labels — same rendering path as mode density */}
      <div style={{ position: "absolute", top: pad.top + plotH + 6, left: 0, right: 0, pointerEvents: "none" }}>
        {freqTicks.map(f => {
          const pct = (f / maxFreq) * 100;
          const offsetPx = (f / maxFreq) * (pad.left + pad.right);
          return (
            <span key={f} style={{
              position: "absolute",
              left: `calc(${pad.left}px + ${pct}% - ${offsetPx}px)`,
              transform: f === maxFreq ? "translateX(-100%)" : "translateX(-50%)",
              fontSize: 10, color: "var(--c-text-3)", fontFamily: "'DM Mono', monospace",
              lineHeight: 1, whiteSpace: "nowrap",
            }}>{f}</span>
          );
        })}
        <span style={{
          position: "absolute",
          left: `calc(100% - ${pad.right}px + 10px)`,
          fontSize: 10, color: "var(--c-text-3)", fontFamily: "'DM Mono', monospace",
          lineHeight: 1, whiteSpace: "nowrap",
        }}>Hz</span>
      </div>
      {/* Schroeder frequency label — HTML overlay for sharp rendering */}
      {schroederFreq !== null && schroederFreq > 0 && schroederFreq <= maxFreq && (() => {
        const pct = (schroederFreq / maxFreq) * 100;
        const offsetPx = (schroederFreq / maxFreq) * (pad.left + pad.right);
        return (
          <span style={{
            position: "absolute", top: 2,
            left: `calc(${pad.left}px + ${pct}% - ${offsetPx}px)`,
            transform: "translateX(-50%)",
            fontSize: 9, color: "#94A3B8", fontFamily: "'DM Mono', monospace",
            lineHeight: 1, whiteSpace: "nowrap", pointerEvents: "none",
          }}>Schroeder ~{Math.round(schroederFreq)} Hz</span>
        );
      })()}
      {tooltip && (
        <div style={{
          position: "absolute", left: tooltip.x + 12, top: tooltip.y - 40,
          background: "#1E2028", border: `1px solid ${typeColors[tooltip.mode.type]}`,
          borderRadius: 6, padding: "6px 10px", pointerEvents: "none", zIndex: 50, whiteSpace: "nowrap",
        }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: typeColors[tooltip.mode.type], fontWeight: 500 }}>
            {tooltip.mode.freq.toFixed(1)} Hz
          </span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--c-text-2)", marginLeft: 8 }}>
            ({tooltip.mode.nx}, {tooltip.mode.ny}, {tooltip.mode.nz}) {typeLabels[tooltip.mode.type]}
          </span>
        </div>
      )}
    </div>
  );
}

const DENSITY_PAD_L = 23; // must match ModeSpectrum pad.left
const DENSITY_PAD_R = 23; // must match ModeSpectrum pad.right

function DensityBars({ modes, maxFreq }) {
  const bands = useMemo(() => modeDensityPerBand(modes, 10, maxFreq), [modes, maxFreq]);
  const maxCount = Math.max(...bands.map((b) => b.count), 1);
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);

  const handleMouseMove = (e, band) => {
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, band });
  };

  const freqTicks = [];
  for (let f = 0; f <= maxFreq; f += 50) freqTicks.push(f);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Bars — inset to match spectrum's pad.left / pad.right exactly */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 60, marginLeft: DENSITY_PAD_L, marginRight: DENSITY_PAD_R }}>
        {bands.map((b, i) => (
          <div key={i}
            onMouseMove={(e) => handleMouseMove(e, b)}
            onMouseLeave={() => setTooltip(null)}
            style={{
              flex: 1,
              height: `${(b.count / maxCount) * 60}px`,
              minHeight: b.count > 0 ? 2 : 0,
              background: b.count > 0 ? `linear-gradient(to top, rgba(232,197,71,0.6), rgba(149,224,108,0.3))` : "transparent",
              borderRadius: "2px 2px 0 0",
              transition: "height 0.3s ease",
              cursor: b.count > 0 ? "crosshair" : "default",
            }} />
        ))}
      </div>
      {/* Labels — same absolute positioning formula as ModeSpectrum */}
      <div style={{ position: "relative", height: 20, marginTop: 3 }}>
        {freqTicks.map(f => {
          const pct = (f / maxFreq) * 100;
          const offsetPx = (f / maxFreq) * (DENSITY_PAD_L + DENSITY_PAD_R);
          return (
            <span key={f} style={{
              position: "absolute",
              left: `calc(${DENSITY_PAD_L}px + ${pct}% - ${offsetPx}px)`,
              transform: f === maxFreq ? "translateX(-100%)" : "translateX(-50%)",
              fontSize: 10, color: "var(--c-text-3)", fontFamily: "'DM Mono', monospace",
              lineHeight: 1, whiteSpace: "nowrap",
            }}>{f}</span>
          );
        })}
        <span style={{
          position: "absolute",
          left: `calc(100% - ${DENSITY_PAD_R}px + 10px)`,
          fontSize: 10, color: "var(--c-text-3)", fontFamily: "'DM Mono', monospace",
          lineHeight: 1, whiteSpace: "nowrap",
        }}>Hz</span>
      </div>
      {tooltip && (
        <div style={{
          position: "absolute", left: tooltip.x + 12, top: tooltip.y - 44,
          background: "#1E2028", border: "1px solid var(--c-border-3)",
          borderRadius: 6, padding: "6px 10px", pointerEvents: "none", zIndex: 50, whiteSpace: "nowrap",
        }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#E8C547", fontWeight: 500 }}>
            {tooltip.band.count} {tooltip.band.count === 1 ? "mode" : "modes"}
          </span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--c-text-2)", marginLeft: 8 }}>
            {tooltip.band.center - 5}–{tooltip.band.center + 5} Hz
          </span>
        </div>
      )}
    </div>
  );
}

// Rotation constraints: keep back wall, left wall, and floor always visible
const AZ_MIN = -(Math.PI - 0.05); // ≈ -175°
const AZ_MAX = -(Math.PI / 2 + 0.05); // ≈ -95°
const EL_MIN = 0.05;              // ≈ 3°
const EL_MAX = Math.PI / 2 - 0.1; // ≈ 80°

// Texture resolution for pressure field per face
const PRESSURE_GRID = 50;
const ISO_PREVIEW_SCALE = 0.18;
const ISO_PREVIEW_STEP = 5.0;
const ISO_SETTLE_SCALE = 0.48;
const ISO_SETTLE_STEP = 2.2;
const ISO_SETTLE_TO_FINAL_MS = 170;
const ISO_FINAL_STEP = 1.0;

// Project the 8 room-box corners to CSS-pixel canvas coordinates for a given pose.
// Returns [{x,y}, ...] for corners [FL-bot, FR-bot, FL-top, FR-top, BL-bot, BR-bot, BL-top, BR-top].
function projectCornersCSS(azArg, elArg, zoomArg, W3, H3, D3, containerW, containerH) {
  const ca = Math.cos(azArg), sa = Math.sin(azArg);
  const ce = Math.cos(elArg), se = Math.sin(elArg);
  function pj(px, py, pz) {
    const x = px - W3 / 2, y = py - H3 / 2, z = pz - D3 / 2;
    return { u: x * ca + z * sa, v: -(y * ce - (-x * sa + z * ca) * se) };
  }
  const pts = [
    pj(0, 0, 0), pj(W3, 0, 0), pj(0, H3, 0), pj(W3, H3, 0),
    pj(0, 0, D3), pj(W3, 0, D3), pj(0, H3, D3), pj(W3, H3, D3),
  ];
  const us = pts.map(p => p.u), vs = pts.map(p => p.v);
  const pad = 32;
  const minU = Math.min(...us) - pad, maxU = Math.max(...us) + pad;
  const minV = Math.min(...vs) - pad, maxV = Math.max(...vs) + pad;
  const svgW = maxU - minU, svgH = maxV - minV;
  const sf = Math.min(containerW / svgW, containerH / svgH) * zoomArg;
  const offX = (containerW - svgW * sf) / 2;
  const offY = (containerH - svgH * sf) / 2;
  return pts.map(p => ({ x: (p.u - minU) * sf + offX, y: (p.v - minV) * sf + offY }));
}

// Solve for the 2D affine transform that maps 3 old-pose screen points to 3 new-pose
// screen points.  Returns { a, b, c, d, e, f } for ctx.setTransform(a, b, c, d, e, f).
// Uses corners 0, 1, 4 (FL-bot, FR-bot, BL-bot) which are never collinear.
function computeAffine(oldPts, newPts) {
  const o0 = oldPts[0], o1 = oldPts[1], o2 = oldPts[4];
  const n0 = newPts[0], n1 = newPts[1], n2 = newPts[4];
  const det = o0.x * (o1.y - o2.y) - o1.x * (o0.y - o2.y) + o2.x * (o0.y - o1.y);
  if (Math.abs(det) < 1e-10) return null;
  const inv = 1 / det;
  return {
    a: ((o1.y - o2.y) * n0.x + (o2.y - o0.y) * n1.x + (o0.y - o1.y) * n2.x) * inv,
    b: ((o1.y - o2.y) * n0.y + (o2.y - o0.y) * n1.y + (o0.y - o1.y) * n2.y) * inv,
    c: ((o2.x - o1.x) * n0.x + (o0.x - o2.x) * n1.x + (o1.x - o0.x) * n2.x) * inv,
    d: ((o2.x - o1.x) * n0.y + (o0.x - o2.x) * n1.y + (o1.x - o0.x) * n2.y) * inv,
    e: ((o1.x * o2.y - o2.x * o1.y) * n0.x + (o2.x * o0.y - o0.x * o2.y) * n1.x + (o0.x * o1.y - o1.x * o0.y) * n2.x) * inv,
    f: ((o1.x * o2.y - o2.x * o1.y) * n0.y + (o2.x * o0.y - o0.x * o2.y) * n1.y + (o0.x * o1.y - o1.x * o0.y) * n2.y) * inv,
  };
}

function RoomViz({ Lx, Ly, Lz, mode, imperial, theme, dimInteracting, activeColormap, setActiveColormap, lut, animPlaying }) {
  const canvasRef = useRef(null);
  const isoCacheRef = useRef(null); // { image: HTMLCanvasElement, az, el, zoom, containerW, containerH, sourceKey, pixelated }
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  const [containerH, setContainerH] = useState(0);
  const [az, setAz] = useState(-Math.PI * 3 / 4); // default -135°
  const [el, setEl] = useState(Math.PI / 6);       // default 30°
  const [isDragging, setIsDragging] = useState(false);
  const [showIso, setShowIso] = useState(true);
  const [zoom, setZoom] = useState(1.0);
  const [isoQualityMode, setIsoQualityMode] = useState('final');
  const dragRef = useRef(null);
  const pinchRef = useRef(null); // tracks 2-finger pinch state: { startDist, startZoom }
  const interactingRef = useRef(false); // true while dragging — read inside render effect
  const settleTimerRef = useRef(null);  // debounce timer for post-drag HQ render
  const finalTimerRef = useRef(null);
  const prevDimInteractingRef = useRef(dimInteracting);
  const targetPoseRef = useRef({ az: -Math.PI * 3 / 4, el: Math.PI / 6, zoom: 1.0 });
  const workerRef = useRef(null);
  const renderGenRef = useRef(0);
  const appliedGenRef = useRef(0);
  const isoWorkerBusyRef = useRef(false); // true while worker is processing a job
  const pendingIsoJobRef = useRef(null);  // latest job waiting to be sent when worker is free
  const poseRafRef = useRef(null); // rAF handle for throttled az/el/zoom state flush
  const texCacheRef = useRef({ key: null, textures: null });
  const crossfadeRef = useRef(null);         // { fromTextures, fromFaceKeys, startTime, duration, rafId }
  const prevModeRef = useRef(null);          // previous mode object — for per-face pattern key comparison
  const prevColormapRef = useRef(null);      // previous colormap — to detect palette changes
  const [crossfadeVersion, setCrossfadeVersion] = useState(0);
  const currentSourceKeyRef = useRef(null);
  const animPhaseRef = useRef(0);   // current animation phase (0..2π), updated by rAF loop
  const animRafRef = useRef(null);  // rAF handle for the play animation loop
  const [animTick, setAnimTick] = useState(0); // increments each animation frame to trigger redraws

  const currentSourceKey = useMemo(() => {
    if (!mode || !showIso) return null;
    return `${mode.nx},${mode.ny},${mode.nz},${Lx},${Ly},${Lz},${activeColormap}`;
  }, [mode, showIso, Lx, Ly, Lz, activeColormap]);

  // rAF-throttled dispatch: coalesce rapid mousemove jobs to at most one per animation frame
  const dispatchIsoJob = useCallback((job) => {
    if (isoWorkerBusyRef.current) {
      // Worker busy — save latest job; it will be flushed the moment onmessage fires
      pendingIsoJobRef.current = job;
      return;
    }
    // Worker free — send immediately, no rAF delay
    pendingIsoJobRef.current = null;
    isoWorkerBusyRef.current = true;
    if (workerRef.current) workerRef.current.postMessage(job);
  }, []);

  const buildIsoJob = useCallback((qualityMode, pose) => {
    if (!mode || !showIso || !currentSourceKey || containerW === 0 || containerH === 0) return null;

    const { az: poseAz, el: poseEl, zoom: poseZoom } = pose;
    const sc = 100 / Math.max(Lx, Lz, Ly);
    const W3 = Lx * sc;
    const H3 = Lz * sc;
    const D3 = Ly * sc;

    const cosAz = Math.cos(poseAz);
    const sinAz = Math.sin(poseAz);
    const cosEl = Math.cos(poseEl);
    const sinEl = Math.sin(poseEl);

    function proj(px, py, pz) {
      const x = px - W3 / 2;
      const y = py - H3 / 2;
      const z = pz - D3 / 2;
      const x1 = x * cosAz + z * sinAz;
      const z1 = -x * sinAz + z * cosAz;
      const y2 = y * cosEl - z1 * sinEl;
      return { u: x1, v: -y2 };
    }

    const P = [
      proj(0, 0, 0),
      proj(W3, 0, 0),
      proj(0, H3, 0),
      proj(W3, H3, 0),
      proj(0, 0, D3),
      proj(W3, 0, D3),
      proj(0, H3, D3),
      proj(W3, H3, D3),
    ];

    const us = P.map((p) => p.u);
    const vs = P.map((p) => p.v);
    const pad = 32;
    const minU = Math.min(...us) - pad;
    const maxU = Math.max(...us) + pad;
    const minV = Math.min(...vs) - pad;
    const maxV = Math.max(...vs) + pad;
    const svgW = maxU - minU;
    const svgH = maxV - minV;

    const sf = Math.min(containerW / svgW, containerH / svgH) * poseZoom;
    const offX = (containerW - svgW * sf) / 2;
    const offY = (containerH - svgH * sf) / 2;

    const ISO_LEVELS = [-0.75, -0.35, 0.35, 0.75];
    const isoColors = ISO_LEVELS.map((lv) => {
      const ci = Math.round(Math.max(0, Math.min(255, (lv + 1) / 2 * 255)));
      return [lut[ci * 3], lut[ci * 3 + 1], lut[ci * 3 + 2]];
    });

    const OS = Math.min(window.devicePixelRatio || 1, 2);
    const quality = qualityMode === 'interactive'
      ? ISO_PREVIEW_SCALE
      : qualityMode === 'settle'
        ? ISO_SETTLE_SCALE
        : 1;

    renderGenRef.current += 1;

    return {
      gen: renderGenRef.current,
      osW: Math.max(2, Math.round(containerW * OS * quality)),
      osH: Math.max(2, Math.round(containerH * OS * quality)),
      containerW,
      containerH,
      offX,
      offY,
      sf,
      minU,
      minV,
      W3,
      H3,
      D3,
      cosAz,
      sinAz,
      cosEl,
      sinEl,
      sourceKey: currentSourceKey,
      az: poseAz,
      el: poseEl,
      zoom: poseZoom,
      nx: mode.nx,
      ny: mode.ny,
      nz: mode.nz,
      STEP: qualityMode === 'interactive'
        ? ISO_PREVIEW_STEP
        : qualityMode === 'settle'
          ? ISO_SETTLE_STEP
          : ISO_FINAL_STEP,
      pixelated: qualityMode !== 'final',
      ISO_LEVELS,
      ISO_ALPHA: 0.5,
      isoColors,
    };
  }, [Lx, Ly, Lz, mode, showIso, containerW, containerH, currentSourceKey, lut]);

  const requestIsoRender = useCallback((qualityMode, pose = targetPoseRef.current) => {
    const job = buildIsoJob(qualityMode, pose);
    if (!job) return;
    dispatchIsoJob(job);
  }, [buildIsoJob, dispatchIsoJob]);

  const beginIsoInteraction = useCallback(() => {
    clearTimeout(settleTimerRef.current);
    clearTimeout(finalTimerRef.current);
    setIsoQualityMode(prev => prev === 'interactive' ? prev : 'interactive');
  }, []);

  const scheduleIsoFinalization = useCallback(() => {
    clearTimeout(settleTimerRef.current);
    clearTimeout(finalTimerRef.current);
    setIsoQualityMode('settle');
    finalTimerRef.current = setTimeout(() => {
      setIsoQualityMode('final');
    }, ISO_SETTLE_TO_FINAL_MS);
  }, []);

  useEffect(() => {
    currentSourceKeyRef.current = currentSourceKey;
    if (!currentSourceKey) {
      pendingIsoJobRef.current = null;
    }
  }, [currentSourceKey]);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (const e of entries) {
        setContainerW(e.contentRect.width);
        setContainerH(e.contentRect.height);
      }
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Create the isosurface worker once; cache results for single-canvas compositing
  useEffect(() => {
    const w = createPublicWorker("isoWorker.js");
    workerRef.current = w;
    w.onmessage = ({ data }) => {
      const isCurrentSource = data.sourceKey === currentSourceKeyRef.current;
      const isNewFrame = data.gen > appliedGenRef.current;

      // Worker finished — mark free and immediately flush any pending job
      isoWorkerBusyRef.current = false;
      if (pendingIsoJobRef.current) {
        const next = pendingIsoJobRef.current;
        pendingIsoJobRef.current = null;
        isoWorkerBusyRef.current = true;
        if (workerRef.current) workerRef.current.postMessage(next);
      }

      if (isCurrentSource && isNewFrame) {
        appliedGenRef.current = data.gen;

        // Build an offscreen canvas from the worker buffer and cache it.
        // The geometry useLayoutEffect will composite this onto the main
        // canvas with an affine correction for any pose delta.
        const img = document.createElement('canvas');
        img.width = data.osW; img.height = data.osH;
        img.getContext('2d').putImageData(
          new ImageData(new Uint8ClampedArray(data.buffer), data.osW, data.osH), 0, 0
        );
        isoCacheRef.current = {
          image: img,
          az: data.az, el: data.el, zoom: data.zoom,
          containerW: data.containerW, containerH: data.containerH,
          sourceKey: data.sourceKey,
          pixelated: data.pixelated,
        };
      }
    };
    return () => {
      clearTimeout(settleTimerRef.current);
      clearTimeout(finalTimerRef.current);
      pendingIsoJobRef.current = null;
      isoWorkerBusyRef.current = false;
      w.terminate();
    };
  }, [dispatchIsoJob]);

  useEffect(() => {
    if (dimInteracting) {
      interactingRef.current = true;
      beginIsoInteraction();
      // Clear iso cache since room shape is changing (sourceKey will differ)
      isoCacheRef.current = null;
      requestIsoRender('interactive', targetPoseRef.current);
    } else if (prevDimInteractingRef.current) {
      interactingRef.current = false;
      scheduleIsoFinalization();
    }
    prevDimInteractingRef.current = dimInteracting;
  }, [dimInteracting, beginIsoInteraction, scheduleIsoFinalization, requestIsoRender]);

  useEffect(() => {
    if (!interactingRef.current) targetPoseRef.current = { az, el, zoom };
  }, [az, el, zoom]);

  useEffect(() => {
    if (!mode || !showIso || containerW === 0 || containerH === 0) return;
    if (interactingRef.current || dimInteracting) return;
    requestIsoRender(isoQualityMode, targetPoseRef.current);
  }, [mode, showIso, containerW, containerH, Lx, Ly, Lz, activeColormap, lut, isoQualityMode, dimInteracting, requestIsoRender]);

  // Animation loop — runs at rAF rate while playing, drives geometry + iso redraws
  useEffect(() => {
    if (!animPlaying || !mode) {
      if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; }
      return;
    }
    function tick() {
      animPhaseRef.current = (performance.now() * 2 * Math.PI / 3000) % (2 * Math.PI);
      setAnimTick(v => v + 1);
      if (showIso) {
        const job = buildIsoJob('interactive', targetPoseRef.current);
        if (job) dispatchIsoJob({ ...job, phase: animPhaseRef.current });
      }
      animRafRef.current = requestAnimationFrame(tick);
    }
    animRafRef.current = requestAnimationFrame(tick);
    return () => { if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; } };
  }, [animPlaying, mode, showIso, buildIsoJob, dispatchIsoJob]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function onWheel(e) {
      e.preventDefault();
      const nextPose = {
        ...targetPoseRef.current,
        zoom: Math.max(0.2, Math.min(4.0, targetPoseRef.current.zoom * (1 - e.deltaY * 0.001))),
      };
      targetPoseRef.current = nextPose;
      if (!poseRafRef.current) {
        poseRafRef.current = requestAnimationFrame(() => {
          poseRafRef.current = null;
          const p = targetPoseRef.current;
          setAz(p.az); setEl(p.el); setZoom(p.zoom);
        });
      }

      if (mode && showIso) {
        interactingRef.current = true;
        beginIsoInteraction();
        requestIsoRender('interactive', nextPose);
      }
      settleTimerRef.current = setTimeout(() => {
        interactingRef.current = false;
        scheduleIsoFinalization();
      }, 150);
    }
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
    };
  }, [beginIsoInteraction, scheduleIsoFinalization, requestIsoRender, mode, showIso]);

  useEffect(() => {
    if (!isDragging) return;
    function schedulePoseFlush() {
      if (poseRafRef.current) return;
      poseRafRef.current = requestAnimationFrame(() => {
        poseRafRef.current = null;
        const p = targetPoseRef.current;
        setAz(p.az); setEl(p.el); setZoom(p.zoom);
      });
    }
    function onMove(ev) {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      const nextPose = {
        ...targetPoseRef.current,
        az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + dx * 0.005)),
        el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + dy * 0.005)),
      };
      targetPoseRef.current = nextPose;
      schedulePoseFlush();
      if (mode && showIso) requestIsoRender('interactive', nextPose);
    }
    function onTouchMove(ev) {
      if (!dragRef.current) return;
      ev.preventDefault();
      if (ev.touches.length === 2 && pinchRef.current) {
        const t0 = ev.touches[0], t1 = ev.touches[1];
        const cx = (t0.clientX + t1.clientX) / 2;
        const cy = (t0.clientY + t1.clientY) / 2;
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        const dx = cx - dragRef.current.startX;
        const dy = cy - dragRef.current.startY;
        const nextPose = {
          ...targetPoseRef.current,
          az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + dx * 0.005)),
          el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + dy * 0.005)),
          zoom: Math.max(0.2, Math.min(4.0, pinchRef.current.startZoom * (dist / pinchRef.current.startDist))),
        };
        targetPoseRef.current = nextPose;
        schedulePoseFlush();
        if (mode && showIso) requestIsoRender('interactive', nextPose);
      } else if (ev.touches.length === 1) {
        const t = ev.touches[0];
        const dx = t.clientX - dragRef.current.startX;
        const dy = t.clientY - dragRef.current.startY;
        const nextPose = {
          ...targetPoseRef.current,
          az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + dx * 0.005)),
          el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + dy * 0.005)),
        };
        targetPoseRef.current = nextPose;
        schedulePoseFlush();
        if (mode && showIso) requestIsoRender('interactive', nextPose);
      }
    }
    function onUp() {
      dragRef.current = null;
      interactingRef.current = false;
      if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
      const p = targetPoseRef.current;
      // Invalidate cached iso unless it already matches the final settled pose.
      // The settle/final render will repopulate with a correct frame.
      const ic = isoCacheRef.current;
      if (ic && !(ic.az === p.az && ic.el === p.el && ic.zoom === p.zoom)) {
        isoCacheRef.current = null;
      }
      setAz(p.az); setEl(p.el); setZoom(p.zoom);
      setIsDragging(false);
      scheduleIsoFinalization();
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, scheduleIsoFinalization, requestIsoRender, mode, showIso]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || containerW === 0 || containerH === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const sc = 100 / Math.max(Lx, Lz, Ly);
    const W3 = Lx * sc, H3 = Lz * sc, D3 = Ly * sc;

    const cosAz = Math.cos(az), sinAz = Math.sin(az);
    const cosEl = Math.cos(el), sinEl = Math.sin(el);

    function proj(px, py, pz) {
      const x = px - W3 / 2, y = py - H3 / 2, z = pz - D3 / 2;
      const x1 = x * cosAz + z * sinAz;
      const z1 = -x * sinAz + z * cosAz;
      const y2 = y * cosEl - z1 * sinEl;
      return { u: x1, v: -y2 };
    }

    // 8 corners: proj(x=length, y=height-up, z=depth)
    const P = [
      proj(0,  0,  0),   // 0 FL-bot
      proj(W3, 0,  0),   // 1 FR-bot
      proj(0,  H3, 0),   // 2 FL-top
      proj(W3, H3, 0),   // 3 FR-top
      proj(0,  0,  D3),  // 4 BL-bot
      proj(W3, 0,  D3),  // 5 BR-bot
      proj(0,  H3, D3),  // 6 BL-top
      proj(W3, H3, D3),  // 7 BR-top
    ];

    const us = P.map(p => p.u), vs = P.map(p => p.v);
    const pad = 32; // padding for the room projection
    const minU = Math.min(...us) - pad, maxU = Math.max(...us) + pad;
    const minV = Math.min(...vs) - pad, maxV = Math.max(...vs) + pad;
    const svgW = maxU - minU, svgH = maxV - minV;

    // Scale to fit both axes; center within the fixed canvas
    const sf = Math.min(containerW / svgW, containerH / svgH) * zoom;
    const offX = (containerW - svgW * sf) / 2;
    const offY = (containerH - svgH * sf) / 2;

    const newW = Math.round(containerW * dpr);
    const newH = Math.round(containerH * dpr);
    if (canvas.style.width !== `${containerW}px`) canvas.style.width = `${containerW}px`;
    if (canvas.style.height !== `${containerH}px`) canvas.style.height = `${containerH}px`;
    if (canvas.width !== newW || canvas.height !== newH) {
      canvas.width = newW;
      canvas.height = newH;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, containerW, containerH);

    // Convert proj coords to canvas logical pixels (centered)
    function toC(p) {
      return { x: (p.u - minU) * sf + offX, y: (p.v - minV) * sf + offY };
    }

    // Face definitions:
    // idx = [C0, C1, C2, C3] indices into P[], where:
    //   (u=0,v=0)→C0  (u=1,v=0)→C1  (u=1,v=1)→C2  (u=0,v=1)→C3
    // physFn(u,v) → [xp, yp, zp] in room physical coordinates
    // Physical axes: xp=length(0..Lx), yp=width/depth(0..Ly), zp=height(0..Lz)
    const [paL, paH, paW] = COLORMAPS[activeColormap]._pa; // pre-parsed [r,g,b] accent triples
    const ra = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;
    const faceDefs = [
      {
        idx: [6, 7, 5, 4], // back wall (depth=Ly): top-left, top-right, bot-right, bot-left
        physFn: (u, v) => [u * Lx, Ly, (1 - v) * Lz],
        stroke: ra(paW, 0.5),
        fill: ra(paW, 0.06),
      },
      {
        idx: [2, 6, 4, 0], // left wall (length=0): top-front, top-back, bot-back, bot-front
        physFn: (u, v) => [0, u * Ly, (1 - v) * Lz],
        stroke: ra(paH, 0.5),
        fill: ra(paH, 0.06),
      },
      {
        idx: [0, 1, 5, 4], // floor (height=0): front-left, front-right, back-right, back-left
        physFn: (u, v) => [u * Lx, v * Ly, 0],
        stroke: ra(paL, 0.5),
        fill: ra(paL, 0.06),
      },
    ];

    // Per-face pattern keys: which mode components actually affect each face's appearance.
    // Back wall (y=Ly): nx and nz vary; ny contributes only as sign cos(ny*π) = parity.
    // Left wall (x=0):  cos(nx*π*0)=1 always, so nx has zero effect — only ny and nz vary.
    // Floor    (z=0):   cos(nz*π*0)=1 always, so nz has zero effect — only nx and ny vary.
    function faceKeys(m, cm) {
      if (!m) return null;
      return [
        `${m.nx},${m.nz},${m.ny % 2},${cm}`, // back wall
        `${m.ny},${m.nz},${cm}`,              // left wall
        `${m.nx},${m.ny},${cm}`,              // floor
      ];
    }

    // Detect mode identity change OR colormap change and start a crossfade
    const modeKey = mode ? `${mode.nx},${mode.ny},${mode.nz}` : null;
    const prevModeKey = prevModeRef.current ? `${prevModeRef.current.nx},${prevModeRef.current.ny},${prevModeRef.current.nz}` : null;
    const colormapChanged = prevColormapRef.current !== null && activeColormap !== prevColormapRef.current;

    if (modeKey !== prevModeKey || colormapChanged) {
      const wasShowingMode = prevModeKey !== null;
      const fromTextures = wasShowingMode ? (texCacheRef.current.textures ?? null) : null;
      const fromFaceKeys = faceKeys(prevModeRef.current, prevColormapRef.current ?? activeColormap);
      if (crossfadeRef.current?.rafId) cancelAnimationFrame(crossfadeRef.current.rafId);

      // Clear stale iso cache — old mode's iso is no longer valid
      isoCacheRef.current = null;

      // Fire crossfade immediately (no longer waiting for iso worker sync)
      if (fromTextures || mode) {
        crossfadeRef.current = { fromTextures, fromFaceKeys, startTime: performance.now(), duration: 380, rafId: null };
        function animCF() {
          const elapsed = performance.now() - crossfadeRef.current.startTime;
          if (elapsed < crossfadeRef.current.duration) {
            crossfadeRef.current.rafId = requestAnimationFrame(animCF);
          } else {
            crossfadeRef.current = null;
          }
          setCrossfadeVersion(v => v + 1);
        }
        crossfadeRef.current.rafId = requestAnimationFrame(animCF);
      } else {
        crossfadeRef.current = null;
      }

      prevModeRef.current = mode;
      prevColormapRef.current = activeColormap;
    } else if (prevColormapRef.current === null) {
      prevColormapRef.current = activeColormap;
    }

    // Build / reuse pressure textures — only rebuilds when mode or room dims change
    const texKey = mode ? `${mode.nx},${mode.ny},${mode.nz},${Lx},${Ly},${Lz},${activeColormap}` : null;
    if (mode && texCacheRef.current.key !== texKey) {
      const G = PRESSURE_GRID;
      const builtRawData = [];
      const builtTextures = faceDefs.map(({ physFn }) => {
        const raw = new Float32Array(G * G);
        const tc = document.createElement('canvas');
        tc.width = G; tc.height = G;
        const tCtx = tc.getContext('2d');
        const img = tCtx.createImageData(G, G);
        for (let j = 0; j < G; j++) {
          for (let i = 0; i < G; i++) {
            const u = (i + 0.5) / G, v = (j + 0.5) / G;
            const [xp, yp, zp] = physFn(u, v);
            const pressure =
              Math.cos(mode.nx * Math.PI * xp / Lx) *
              Math.cos(mode.ny * Math.PI * yp / Ly) *
              Math.cos(mode.nz * Math.PI * zp / Lz);
            const pi = j * G + i;
            raw[pi] = pressure;
            const ci = Math.round(Math.max(0, Math.min(255, (pressure + 1) * 127.5)));
            const k = pi * 4;
            img.data[k]   = lut[ci * 3];
            img.data[k+1] = lut[ci * 3 + 1];
            img.data[k+2] = lut[ci * 3 + 2];
            img.data[k+3] = 255;
          }
        }
        tCtx.putImageData(img, 0, 0);
        builtRawData.push(raw);
        return tc;
      });
      texCacheRef.current = { key: texKey, textures: builtTextures, rawData: builtRawData };
    }
    const cachedTextures = mode ? texCacheRef.current.textures : null;

    // When animating, build per-frame face textures by applying cos(phase) to cached raw pressures
    const animTextures = (animPlaying && texCacheRef.current.rawData)
      ? texCacheRef.current.rawData.map((raw) => {
          const G = PRESSURE_GRID;
          const cosP = Math.cos(animPhaseRef.current);
          const tc = document.createElement('canvas');
          tc.width = G; tc.height = G;
          const tCtx = tc.getContext('2d');
          const img = tCtx.createImageData(G, G);
          for (let p = 0; p < G * G; p++) {
            const pressure = raw[p] * cosP;
            const ci = Math.round(Math.max(0, Math.min(255, (pressure + 1) * 127.5)));
            const k = p * 4;
            img.data[k]   = lut[ci * 3];
            img.data[k+1] = lut[ci * 3 + 1];
            img.data[k+2] = lut[ci * 3 + 2];
            img.data[k+3] = 255;
          }
          tCtx.putImageData(img, 0, 0);
          return tc;
        })
      : null;

    // Compute crossfade t (0 = fully from, 1 = fully to) with ease-in-out
    const cf = crossfadeRef.current;
    let cfT = 1;
    if (cf) {
      const raw = Math.min(1, (performance.now() - cf.startTime) / cf.duration);
      cfT = raw < 0.5 ? 2 * raw * raw : 1 - (-2 * raw + 2) ** 2 / 2;
    }
    const isBlending = cf !== null && cfT < 1;
    const toFaceKeys = faceKeys(mode, activeColormap);

    for (let fi = 0; fi < faceDefs.length; fi++) {
      const { idx, stroke, fill } = faceDefs[fi];
      const [C0, C1, C2, C3] = idx.map(i => toC(P[i]));

      // Per-face optimization: skip animation entirely when this face's pattern key is unchanged
      const facePatternChanged = !cf?.fromFaceKeys || !toFaceKeys ||
        cf.fromFaceKeys[fi] !== toFaceKeys[fi];
      const shouldBlend = isBlending && facePatternChanged;

      if (mode || shouldBlend) {
        const G = PRESSURE_GRID;
        const a  = (C1.x - C0.x) / G, bv = (C1.y - C0.y) / G;
        const c  = (C3.x - C0.x) / G, d  = (C3.y - C0.y) / G;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(C0.x, C0.y); ctx.lineTo(C1.x, C1.y);
        ctx.lineTo(C2.x, C2.y); ctx.lineTo(C3.x, C3.y);
        ctx.closePath();
        ctx.clip();
        ctx.transform(a, bv, c, d, C0.x, C0.y);

        // Use animated texture when playing, otherwise the cached static texture
        const toTex = animTextures?.[fi] ?? cachedTextures?.[fi] ?? null;
        if (shouldBlend && cf.fromTextures && cachedTextures) {
          // Mode A → Mode B: draw from at full opacity, overlay to at cfT.
          // source-over math: result = cfT*to + (1-cfT)*from — always fully opaque.
          // Pixels where from==to evaluate to the same color at any cfT, zero darkening.
          ctx.drawImage(cf.fromTextures[fi], 0, 0);
          ctx.globalAlpha = cfT;
          ctx.drawImage(toTex, 0, 0);
          ctx.globalAlpha = 1;
        } else if (shouldBlend && !cf.fromTextures && cachedTextures) {
          // null → Mode: fade in new texture from transparent
          ctx.globalAlpha = cfT;
          ctx.drawImage(toTex, 0, 0);
          ctx.globalAlpha = 1;
        } else if (shouldBlend && cf.fromTextures && !cachedTextures) {
          // Mode → null: fade out old texture
          ctx.globalAlpha = 1 - cfT;
          ctx.drawImage(cf.fromTextures[fi], 0, 0);
          ctx.globalAlpha = 1;
        } else if (toTex) {
          // Steady state: animated or static texture
          ctx.drawImage(toTex, 0, 0);
        }

        ctx.restore();
        ctx.beginPath();
        ctx.moveTo(C0.x, C0.y); ctx.lineTo(C1.x, C1.y);
        ctx.lineTo(C2.x, C2.y); ctx.lineTo(C3.x, C3.y);
        ctx.closePath();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(C0.x, C0.y); ctx.lineTo(C1.x, C1.y);
        ctx.lineTo(C2.x, C2.y); ctx.lineTo(C3.x, C3.y);
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }

    // Composite cached isosurface onto the SAME canvas as geometry.
    // The cached iso was ray-marched at a specific pose.  If the current pose
    // matches exactly, draw it directly.  If we're actively dragging and the
    // pose delta is small, apply an affine correction to keep it visible.
    // If we're NOT dragging (settled state), only show an exact-pose cache
    // so we never display warped volumetric content after release.
    const isoCache = isoCacheRef.current;
    if (isoCache && showIso && mode && isoCache.sourceKey === currentSourceKey) {
      const poseExact = isoCache.az === az && isoCache.el === el && isoCache.zoom === zoom;
      // Separate angle delta from zoom delta so they don't contaminate each other
      const angleDelta = Math.abs(isoCache.az - az) + Math.abs(isoCache.el - el);
      const zoomRatio = isoCache.zoom !== 0 ? Math.abs(zoom / isoCache.zoom - 1) : 1;
      // During drag: allow affine reprojection up to ~10° combined angle + 15% zoom.
      // After release (settled): only show exact-pose frames.
      const shouldDraw = poseExact || (interactingRef.current && angleDelta < 0.18 && zoomRatio < 0.15);

      if (shouldDraw) {
        const newCorners = [0, 1, 2, 3, 4, 5, 6, 7].map(i => toC(P[i]));
        let M = null;
        if (!poseExact) {
          const oldCorners = projectCornersCSS(
            isoCache.az, isoCache.el, isoCache.zoom,
            W3, H3, D3, isoCache.containerW, isoCache.containerH
          );
          M = computeAffine(oldCorners, newCorners);
        }

        ctx.save();
        // Clip to room silhouette (convex hull: corners 0→1→5→7→6→2)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.beginPath();
        const hull = [0, 1, 5, 7, 6, 2].map(i => newCorners[i]);
        ctx.moveTo(hull[0].x, hull[0].y);
        for (let hi = 1; hi < hull.length; hi++) ctx.lineTo(hull[hi].x, hull[hi].y);
        ctx.closePath();
        ctx.clip();

        if (M) {
          // Apply affine correction for stale pose during drag
          ctx.setTransform(M.a * dpr, M.b * dpr, M.c * dpr, M.d * dpr, M.e * dpr, M.f * dpr);
        }
        ctx.imageSmoothingEnabled = !isoCache.pixelated;
        ctx.imageSmoothingQuality = isoCache.pixelated ? 'low' : 'high';
        ctx.drawImage(isoCache.image, 0, 0, isoCache.containerW, isoCache.containerH);
        ctx.restore();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    }
    if (!mode || !showIso) {
      clearTimeout(finalTimerRef.current);
      setIsoQualityMode('final');
    }

    // Phantom dashed edges for depth perception
    ctx.strokeStyle = theme === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.13)';
    ctx.lineWidth = 0.6;
    ctx.setLineDash([3, 3]);
    for (const [ai, bi] of [[2, 3], [3, 1], [3, 7]]) {
      const ca = toC(P[ai]), cb = toC(P[bi]);
      ctx.beginPath(); ctx.moveTo(ca.x, ca.y); ctx.lineTo(cb.x, cb.y); ctx.stroke();
    }
    ctx.setLineDash([]);

    // Dimension labels — offset perpendicularly outward from each edge
    function labelPos(iA, iB, offset = 16) {
      const p1 = P[iA], p2 = P[iB];
      const mu = (p1.u + p2.u) / 2, mv = (p1.v + p2.v) / 2;
      const du = p2.u - p1.u, dv = p2.v - p1.v;
      const len = Math.sqrt(du * du + dv * dv) || 1;
      const n1u = -dv / len, n1v = du / len;
      const dot = n1u * mu + n1v * mv;
      const [nu, nv] = dot >= 0 ? [n1u, n1v] : [-n1u, -n1v];
      return toC({ u: mu + nu * offset, v: mv + nv * offset });
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${Math.round(6 * sf)}px "DM Mono", monospace`;

    const dimLabel = (val) => imperial ? formatFtInSymbol(val) : `${val.toFixed(1)}m`;

    const ll = labelPos(0, 1);
    ctx.fillStyle = ra(paL, 0.9);
    ctx.fillText(`L = ${dimLabel(Lx)}`, ll.x, ll.y);

    const lh = labelPos(5, 7, 24);
    ctx.fillStyle = ra(paH, 0.9);
    ctx.fillText(`H = ${dimLabel(Lz)}`, lh.x, lh.y);

    const lw = labelPos(1, 5);
    ctx.fillStyle = ra(paW, 0.9);
    ctx.fillText(`W = ${dimLabel(Ly)}`, lw.x, lw.y);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [az, el, Lx, Ly, Lz, mode, showIso, containerW, containerH, imperial, theme, zoom, activeColormap, lut, crossfadeVersion, animTick, animPlaying, currentSourceKey]);

  return (
    <div ref={containerRef} style={{ width: '100%', flex: 1, minHeight: 0, position: 'relative' }}>
      <canvas
        ref={canvasRef}
        aria-label="Room geometry 3D view — drag to rotate, scroll or pinch to zoom"
        style={{ display: 'block', cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
        onMouseDown={e => {
          dragRef.current = { startX: e.clientX, startY: e.clientY, startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el };
          interactingRef.current = true;
          beginIsoInteraction();
          setIsDragging(true);
          e.preventDefault();
        }}
        onTouchStart={e => {
          if (e.touches.length === 2) {
            const t0 = e.touches[0], t1 = e.touches[1];
            const cx = (t0.clientX + t1.clientX) / 2;
            const cy = (t0.clientY + t1.clientY) / 2;
            const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
            dragRef.current = { startX: cx, startY: cy, startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el };
            pinchRef.current = { startDist: dist, startZoom: targetPoseRef.current.zoom };
          } else {
            const t = e.touches[0];
            dragRef.current = { startX: t.clientX, startY: t.clientY, startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el };
            pinchRef.current = null;
          }
          interactingRef.current = true;
          beginIsoInteraction();
          setIsDragging(true);
          e.preventDefault();
        }}
      />
      {/* Colormap picker — always visible in top-left */}
      <div style={{ position: 'absolute', top: 8, left: 8 }}>
        <ColormapPicker value={activeColormap} onChange={setActiveColormap} theme={theme} />
      </div>
      {mode && (
        <button
          aria-pressed={showIso}
          onClick={() => setShowIso(v => !v)}
          style={{
            position: 'absolute', top: 8, right: 8,
            background: showIso ? hexRgba(COLORMAPS[activeColormap].accents[0], 0.12) : 'rgba(138,143,152,0.1)',
            border: `1px solid ${showIso ? hexRgba(COLORMAPS[activeColormap].accents[0], 0.4) : 'rgba(138,143,152,0.25)'}`,
            color: showIso ? COLORMAPS[activeColormap].accents[0] : '#5A5F68',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
            fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          ISO {showIso ? 'ON' : 'OFF'}
        </button>
      )}
    </div>
  );
}

function ModeTable({ modes, selectedMode, onSelectMode }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? modes : modes.slice(0, 20);
  const HEADER_BG = "var(--c-card)";

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Scroll container */}
      <div
        className="mode-scroll"
        style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
      >
        {modes.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 120, gap: 6, padding: 20 }}>
            <span style={{ fontSize: 22, opacity: 0.25 }}>∅</span>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "var(--c-text-3)", textAlign: "center" }}>No modes visible</span>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 9, color: "var(--c-text-3)", textAlign: "center", opacity: 0.7 }}>Enable a mode type above</span>
          </div>
        ) : (
        <div role="grid" style={{
          display: "grid",
          gridTemplateColumns: "clamp(60px, 50%, 90px) 1fr auto",
          width: "100%",
          fontSize: 11,
          fontFamily: "'DM Mono', monospace",
        }}>
          {/* Sticky header row */}
          <div style={{ position: "sticky", top: 0, zIndex: 1, background: HEADER_BG, color: "var(--c-text-3)", padding: "4px 0", borderBottom: "1px solid var(--c-border-3)", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>Mode</div>
          <div style={{ position: "sticky", top: 0, zIndex: 1, background: HEADER_BG, color: "var(--c-text-3)", padding: "4px 14px 4px 0", textAlign: "center", borderBottom: "1px solid var(--c-border-3)", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>Freq</div>
          <div style={{ position: "sticky", top: 0, zIndex: 1, background: HEADER_BG, color: "var(--c-text-3)", padding: "4px 8px 4px 0", textAlign: "right", borderBottom: "1px solid var(--c-border-3)", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>Type</div>
          {/* Data rows */}
          {displayed.map((m, i) => {
            const sel = selectedMode &&
              selectedMode.nx === m.nx &&
              selectedMode.ny === m.ny &&
              selectedMode.nz === m.nz;
            const bg = sel ? "rgba(232,197,71,0.08)" : "transparent";
            const textColor = sel ? "#E8C547" : "var(--c-text)";
            return (
              <div key={i} role="row" style={{ display: "contents" }}>
                <div role="gridcell" tabIndex={0} className="mode-data-cell" onClick={() => onSelectMode(m)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectMode(m); }}} aria-selected={sel} style={{ color: textColor, padding: "3px 0", background: bg, cursor: "pointer", borderLeft: sel ? "2px solid #E8C547" : "2px solid transparent", paddingLeft: 4, whiteSpace: "nowrap" }}>
                  ({m.nx}, {m.ny}, {m.nz})
                </div>
                <div role="gridcell" className="mode-data-cell" onClick={() => onSelectMode(m)} style={{ color: textColor, padding: "3px 10px 3px 0", textAlign: "center", background: bg, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {m.freq.toFixed(2)} Hz
                </div>
                <div role="gridcell" className="mode-data-cell" onClick={() => onSelectMode(m)} style={{ color: sel ? "#E8C547" : typeColors[m.type], padding: "3px 6px 3px 0", textAlign: "right", background: bg, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {typeLabels[m.type]}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
      {/* Count line + Show all / show fewer button — outside scroll area, never scrolls away */}
      {modes.length > 20 && (
        <div style={{ padding: "4px 0 2px", textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--c-text-3)" }}>
          {showAll ? `all ${modes.length} modes` : `showing 20 of ${modes.length}`}
        </div>
      )}
      {modes.length > 20 && (
        <button
          onClick={() => setShowAll(!showAll)}
          style={{
            flexShrink: 0, marginTop: 4, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(138,143,152,0.2)",
            color: "var(--c-text-2)", padding: "3px 8px", borderRadius: 5, cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, fontSize: 10,
            transition: "all 0.15s", letterSpacing: "0.02em",
          }}
        >
          {showAll ? "Show fewer" : `Show all ${modes.length} modes`}
        </button>
      )}
    </div>
  );
}

function PressureHeatmap({ mode, Lx, Ly, Lz, lut, activeColormap, imperial, animPlaying, glPhaseRef, phaseResetTrigger }) {
  const canvasRef = useRef(null);
  const fromCanvasRef = useRef(null);
  const prevModeKeyRef = useRef(null);
  const prevLutRef = useRef(null);
  const [plane, setPlane] = useState("XY");
  const [slicePos, setSlicePos] = useState(0.5);
  const lastTapRef = useRef(0);
  const animPhaseRef = useRef(0);
  const animRafRef = useRef(null);
  const [animTick, setAnimTick] = useState(0);
  const spatialCacheRef = useRef({ key: null, data: null });
  const BUF = 400;

  // Animation rAF loop — increments animTick to trigger redraws at 60fps while playing
  useEffect(() => {
    if (!animPlaying || !mode) {
      if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; }
      return;
    }
    function tick() {
      animPhaseRef.current = glPhaseRef ? glPhaseRef.current : (performance.now() * 2 * Math.PI / 3000) % (2 * Math.PI);
      setAnimTick(v => v + 1);
      animRafRef.current = requestAnimationFrame(tick);
    }
    animRafRef.current = requestAnimationFrame(tick);
    return () => { if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; } };
  }, [animPlaying, mode, glPhaseRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mode) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { nx, ny, nz } = mode;

    // Crossfade when mode identity or colormap (lut) changes — not on slice drag or anim tick
    const modeKey = `${nx},${ny},${nz}`;
    const shouldAnimate =
      (prevModeKeyRef.current !== null && modeKey !== prevModeKeyRef.current) ||
      (prevLutRef.current !== null && lut !== prevLutRef.current);
    prevModeKeyRef.current = modeKey;
    prevLutRef.current = lut;

    if (shouldAnimate) {
      const fc = fromCanvasRef.current;
      if (fc && canvas.width > 0) {
        fc.width = canvas.width;
        fc.height = canvas.height;
        fc.getContext('2d')?.drawImage(canvas, 0, 0);
        fc.style.transition = 'none';
        fc.style.opacity = '1';
        fc.getBoundingClientRect(); // force reflow to commit opacity=1 before transitioning
        fc.style.transition = 'opacity 380ms ease-in-out';
        fc.style.opacity = '0';
      }
    }

    if (canvas.width !== BUF || canvas.height !== BUF) {
      canvas.width = BUF;
      canvas.height = BUF;
    }

    // Rebuild spatial cache when mode, plane, slice, or dims change (not on anim tick)
    const spatialKey = `${nx},${ny},${nz},${plane},${slicePos.toFixed(3)},${Lx},${Ly},${Lz}`;
    if (spatialCacheRef.current.key !== spatialKey) {
      const raw = new Float32Array(BUF * BUF);
      let si = 0;
      for (let row = 0; row < BUF; row++) {
        for (let col = 0; col < BUF; col++) {
          const u = col / (BUF - 1);
          const v = 1 - row / (BUF - 1);
          let x, y, z;
          if (plane === "XY") { x = u * Lx; y = v * Ly; z = slicePos * Lz; }
          else if (plane === "XZ") { x = u * Lx; y = slicePos * Ly; z = v * Lz; }
          else { x = slicePos * Lx; y = u * Ly; z = v * Lz; }
          raw[si++] = Math.cos(nx * Math.PI * x / Lx) * Math.cos(ny * Math.PI * y / Ly) * Math.cos(nz * Math.PI * z / Lz);
        }
      }
      spatialCacheRef.current = { key: spatialKey, data: raw };
    }

    // Apply time-phase multiplier: animated = spatial * cos(phase); static = spatial * 1
    const cosPhase = animPlaying ? Math.cos(animPhaseRef.current) : Math.cos(glPhaseRef.current);
    const rawSpatial = spatialCacheRef.current.data;
    const imageData = ctx.createImageData(BUF, BUF);
    const imgData = imageData.data;
    for (let i = 0; i < BUF * BUF; i++) {
      const p = rawSpatial[i] * cosPhase;
      const ci = Math.round(Math.max(0, Math.min(255, (p + 1) * 127.5)));
      const k = i * 4;
      imgData[k]   = lut[ci * 3];
      imgData[k+1] = lut[ci * 3 + 1];
      imgData[k+2] = lut[ci * 3 + 2];
      imgData[k+3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, plane, slicePos, Lx, Ly, Lz, lut, animTick, animPlaying, phaseResetTrigger]);

  const dim = (v) => imperial ? formatFtInSymbol(v) : `${v.toFixed(2)} m`;
  const sliceLabel =
    plane === "XY" ? `z = ${dim(slicePos * Lz)}` :
    plane === "XZ" ? `y = ${dim(slicePos * Ly)}` :
                     `x = ${dim(slicePos * Lx)}`;

  const axisLabels =
    plane === "XY" ? { h: "x — length →", v: "y — width →" } :
    plane === "XZ" ? { h: "x — length →", v: "z — height →" } :
                     { h: "y — width →",  v: "z — height →" };

  const monoStyle = { fontFamily: "'DM Mono', monospace" };
  const labelStyle = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 0, flexWrap: "wrap" }}>
          <span style={{ ...labelStyle, fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--c-text-3)" }}>
            Pressure Field
          </span>
          <span style={{ ...monoStyle, fontSize: 11, color: typeColors[mode.type], marginLeft: 10, fontWeight: 500 }}>
            ({mode.nx},{mode.ny},{mode.nz})
          </span>
          <span style={{ ...monoStyle, fontSize: 11, color: "var(--c-text-2)", marginLeft: 7 }}>
            {mode.freq.toFixed(1)} Hz
          </span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {["XY", "XZ", "YZ"].map((p) => (
            <button
              key={p}
              onClick={() => setPlane(p)}
              style={{
                ...labelStyle, fontSize: 10, fontWeight: 600,
                letterSpacing: "0.06em", textTransform: "uppercase",
                background: plane === p ? "rgba(232,197,71,0.12)" : "transparent",
                border: `1px solid ${plane === p ? "rgba(232,197,71,0.45)" : "rgba(138,143,152,0.2)"}`,
                color: plane === p ? "#E8C547" : "var(--c-text-3)",
                padding: "3px 9px", borderRadius: 4, cursor: "pointer",
                transition: "all 0.12s",
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Canvas + colorbar */}
      <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
        {/* Vertical axis label */}
        <div style={{ display: "flex", alignItems: "center", width: 12, flexShrink: 0 }}>
          <span style={{
            ...monoStyle, fontSize: 9, color: "var(--c-text-3)",
            writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap",
          }}>
            {axisLabels.v}
          </span>
        </div>

        {/* Heatmap canvas */}
        <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
          <canvas
            ref={canvasRef}
            style={{ width: "100%", display: "block", borderRadius: 4, border: "1px solid var(--c-border-2)" }}
          />
          <canvas
            ref={fromCanvasRef}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                     borderRadius: 4, pointerEvents: "none", opacity: 0 }}
          />
        </div>

        {/* Colorbar — reflects active colormap */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 20, flexShrink: 0 }}>
          <span style={{ ...monoStyle, fontSize: 9, color: "var(--c-text-3)" }}>+1</span>
          <div style={{
            flex: 1, width: 12, borderRadius: 3,
            background: colormapGradientVerticalCSS(activeColormap),
            border: "1px solid var(--c-border-2)",
          }} />
          <span style={{ ...monoStyle, fontSize: 9, color: "var(--c-text-3)" }}>−1</span>
        </div>
      </div>

      {/* Horizontal axis label */}
      <div style={{ textAlign: "center", marginTop: 3, paddingLeft: 22 }}>
        <span style={{ ...monoStyle, fontSize: 9, color: "var(--c-text-3)" }}>{axisLabels.h}</span>
      </div>

      {/* Slice position slider */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <span style={{ ...labelStyle, fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--c-text-3)" }}>
            Slice
          </span>
          <span style={{ ...monoStyle, fontSize: 12, color: "var(--c-text)", fontWeight: 500 }}>
            {sliceLabel}
          </span>
        </div>
        <input
          type="range" min={0} max={1} step={0.005} value={slicePos}
          onChange={(e) => setSlicePos(parseFloat(e.target.value))}
          onDoubleClick={() => setSlicePos(0.5)}
          onTouchEnd={() => {
            const now = Date.now();
            if (now - lastTapRef.current < 300) setSlicePos(0.5);
            lastTapRef.current = now;
          }}
          style={{ width: "100%", accentColor: "#E8C547" }}
        />
      </div>
    </div>
  );
}

export default function App() {
  // CHUNK_1_HIDDEN: const [activeTab, setActiveTab] = useState('acoustic'); // 'acoustic' | 'structural'
  const [Lx, setLx] = useState(INITIAL_HASH.lx ?? 7.0);
  const [Ly, setLy] = useState(INITIAL_HASH.ly ?? 5.0);
  const [Lz, setLz] = useState(INITIAL_HASH.lz ?? 3.0);
  const [maxFreq, setMaxFreq] = useState(INITIAL_HASH.f ?? 300);
  const [selectedMode, setSelectedMode] = useState(null);
  const [animPlaying, setAnimPlaying] = useState(false);
  const glPhaseRef = useRef(0);
  const handleGlPhaseUpdate = useCallback((phase) => {
    glPhaseRef.current = phase;
  }, []);
  const [phaseResetCount, setPhaseResetCount] = useState(0);
  const [showResetButton, setShowResetButton] = useState(false);
  const handlePhaseReset = useCallback(() => {
    glPhaseRef.current = 0;
    setAnimPlaying(false);
    setPhaseResetCount(c => c + 1);
    setShowResetButton(false);
  }, []);
  const [visibleTypes, setVisibleTypes] = useState({ axial: true, tangential: true, oblique: true });
  const [rt60, setRt60] = useState(INITIAL_HASH.rt60 ?? 0.4);
  const [units, setUnits] = useState(INITIAL_HASH.units ?? "metric");
  const imperial = units === "imperial";
  const [showProps, setShowProps] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [theme, setTheme] = useState(INITIAL_HASH.theme ?? "dark");
  // const [copied, setCopied] = useState(false); // used by Share button (disabled)
  const [activeColormap, setActiveColormap] = useState(DEFAULT_COLORMAP);
  const lut = useMemo(() => buildLUT(activeColormap), [activeColormap]);
  const [dimInteracting, setDimInteracting] = useState(false);
  const dimSettleTimerRef = useRef(null);
  const initialModeRef = useRef(INITIAL_HASH.mode);
  const onDimInteractStart = () => { clearTimeout(dimSettleTimerRef.current); setDimInteracting(true); };
  const onDimInteractEnd   = () => { clearTimeout(dimSettleTimerRef.current); dimSettleTimerRef.current = setTimeout(() => setDimInteracting(false), 80); };

  // Compute modes synchronously — calculateModes is sub-millisecond, no worker needed
  const modes = useMemo(() => calculateModes(Lx, Ly, Lz, maxFreq), [Lx, Ly, Lz, maxFreq]);

  // Defer only for genuinely expensive downstream consumers (acousticMetrics, PressureHeatmap)
  const deferredModes = useDeferredValue(modes);
  const deferredRt60 = useDeferredValue(rt60);

  const filteredModes = useMemo(() => modes.filter(m => visibleTypes[m.type]), [modes, visibleTypes]);

  // Pre-grouped modes by type — avoids repeated .filter() in render loops
  const modesByType = useMemo(() => {
    const groups = { axial: [], tangential: [], oblique: [] };
    for (const m of modes) groups[m.type].push(m);
    return groups;
  }, [modes]);

  const filteredModesByType = useMemo(() => {
    const groups = { axial: [], tangential: [], oblique: [] };
    for (const m of filteredModes) groups[m.type].push(m);
    return groups;
  }, [filteredModes]);

  // Reset selected mode if it's no longer in the current mode list
  useEffect(() => {
    if (selectedMode) {
      const still = modes.find(
        (m) => m.nx === selectedMode.nx && m.ny === selectedMode.ny && m.nz === selectedMode.nz
      );
      if (!still) setSelectedMode(null);
    }
  }, [modes, selectedMode]);

  // Stop animation and clear reset-button state when mode is deselected
  useEffect(() => {
    if (!selectedMode) { setAnimPlaying(false); setShowResetButton(false); }
  }, [selectedMode]);

  // Clear selection if its type is hidden
  useEffect(() => {
    if (selectedMode && !visibleTypes[selectedMode.type]) setSelectedMode(null);
  }, [visibleTypes, selectedMode]);

  // Restore selected mode from URL hash after modes are first computed.
  // selectedMode is intentionally omitted: once initialModeRef.current is cleared this
  // effect is a permanent no-op, so including it would only add unnecessary re-runs.
  useEffect(() => {
    if (!initialModeRef.current || selectedMode) return;
    const parts = initialModeRef.current.split(',').map(Number);
    if (parts.length === 3 && parts.every(isFinite)) {
      const [nx, ny, nz] = parts;
      const m = modes.find(m => m.nx === nx && m.ny === ny && m.nz === nz);
      if (m) { setSelectedMode(m); initialModeRef.current = null; }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modes]);

  // Sync state to URL hash (replaceState — no history pollution)
  // useEffect(() => {
  //   const p = new URLSearchParams();
  //   p.set('lx', Lx.toFixed(2)); p.set('ly', Ly.toFixed(2)); p.set('lz', Lz.toFixed(2));
  //   p.set('f', maxFreq); p.set('rt60', rt60.toFixed(2));
  //   if (theme !== 'dark') p.set('theme', theme);
  //   if (units !== 'metric') p.set('units', units);
  //   if (selectedMode) p.set('mode', `${selectedMode.nx},${selectedMode.ny},${selectedMode.nz}`);
  //   window.history.replaceState(null, '', '#' + p.toString());
  // }, [Lx, Ly, Lz, maxFreq, rt60, theme, units, selectedMode]);

  const { V, S } = getBoltArea(Lx, Ly, Lz);

  const acousticMetrics = useMemo(() => {
    const schroeder = Math.round(2000 * Math.sqrt(deferredRt60 / Math.max(V, 0.001)));
    const bandwidth = 2.2 / deferredRt60;
    const sortedFreqs = [...deferredModes].map(m => m.freq).sort((a, b) => a - b);
    const gaps = [];
    for (let i = 1; i < sortedFreqs.length; i++) {
      gaps.push({ gap: sortedFreqs[i] - sortedFreqs[i - 1], f1: sortedFreqs[i - 1], f2: sortedFreqs[i] });
    }
    const avgSpacing = gaps.length > 0 ? gaps.reduce((s, g) => s + g.gap, 0) / gaps.length : 1;
    const mof = bandwidth / avgSpacing;
    const critDist = 0.057 * Math.sqrt(V / deferredRt60);
    const topGaps = [...gaps].sort((a, b) => b.gap - a.gap).slice(0, 3);
    return { schroeder, bandwidth, mof, critDist, topGaps, avgSpacing };
  }, [deferredModes, deferredRt60, V]);

  const { axialCount, tangentialCount, obliqueCount } = useMemo(() => ({
    axialCount: modesByType.axial.length,
    tangentialCount: modesByType.tangential.length,
    obliqueCount: modesByType.oblique.length,
  }), [modesByType]);

  const ratioLH = (Lx / Lz).toFixed(2);
  const ratioWH = (Ly / Lz).toFixed(2);

  const cardStyle = {
    background: "var(--c-card)",
    borderRadius: 12,
    padding: 14,
    border: "1px solid var(--c-border)",
    marginBottom: 12,
  };

  const sectionLabel = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "var(--c-text-3)",
    display: "block",
    marginBottom: 8,
  };

  return (
    <div className="app-root" data-theme={theme} style={{ minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <style>{`
        :root {
          --c-bg: #13141A; --c-card: #1A1B22; --c-text: #E8ECF1;
          --c-text-2: #8A8F98; --c-text-3: #5A5F68;
          --c-border: rgba(138,143,152,0.08); --c-border-2: rgba(138,143,152,0.2); --c-border-3: rgba(138,143,152,0.3);
          --c-surface: rgba(138,143,152,0.08); --c-track: rgba(138,143,152,0.18);
        }
        [data-theme="light"] {
          --c-bg: #F0F1F6; --c-card: #FFFFFF; --c-text: #13141A;
          --c-text-2: #5A5F68; --c-text-3: #9CA3AF;
          --c-border: rgba(0,0,0,0.08); --c-border-2: rgba(0,0,0,0.15); --c-border-3: rgba(0,0,0,0.2);
          --c-surface: rgba(0,0,0,0.05); --c-track: rgba(0,0,0,0.12);
        }
        html, body { overflow-x: clip; max-width: 100%; }
        input[type=range] { -webkit-appearance: none; appearance: none; height: 3px; background: var(--c-track); border-radius: 2px; outline: none; cursor: pointer; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%; background: #E8C547; cursor: pointer; box-shadow: 0 0 6px rgba(232,197,71,0.35); transition: transform 0.1s, box-shadow 0.1s; }
        input[type=range]::-webkit-slider-thumb:hover { transform: scale(1.2); box-shadow: 0 0 10px rgba(232,197,71,0.55); }
        input[type=range]:active::-webkit-slider-thumb { transform: scale(1.1); box-shadow: 0 0 14px rgba(232,197,71,0.7); }
        input[type=range]::-moz-range-track { height: 3px; background: var(--c-track); border-radius: 2px; border: none; }
        input[type=range]::-moz-range-thumb { width: 14px; height: 14px; border-radius: 50%; background: #E8C547; border: none; cursor: pointer; box-shadow: 0 0 6px rgba(232,197,71,0.35); }
        input[type=number].num-input { -moz-appearance: textfield; }
        input[type=number].num-input::-webkit-inner-spin-button,
        input[type=number].num-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number].num-input:focus { outline: 1px solid rgba(232,197,71,0.5); border-color: rgba(232,197,71,0.4) !important; }
        .mode-data-cell:focus { outline: 1px solid rgba(232,197,71,0.4); outline-offset: -1px; }
        * { box-sizing: border-box; }
        .main-grid > * { min-width: 0; }
        .app-root { padding: 10px 20px; background: var(--c-bg); color: var(--c-text); }
        .app-card { transition: border-color 0.2s; }
        .app-card:hover { border-color: var(--c-border-2) !important; }
        .mode-scroll::-webkit-scrollbar { width: 8px; }
        .mode-scroll::-webkit-scrollbar-track { background: var(--c-surface); border-radius: 4px; }
        .mode-scroll::-webkit-scrollbar-thumb { background: var(--c-border-2); border-radius: 4px; }
        .mode-scroll::-webkit-scrollbar-thumb:hover { background: var(--c-border-3); }
        .theme-toggle { background: none; border: 1px solid var(--c-border-2); border-radius: 8px; color: var(--c-text-3); cursor: pointer; font-size: 15px; line-height: 1; padding: 6px 10px; transition: border-color 0.15s, color 0.15s; }
        .theme-toggle:hover { border-color: var(--c-border-3); color: var(--c-text-2); }
        @keyframes value-pop { 0% { opacity: 0; transform: translateY(-3px); } 100% { opacity: 1; transform: translateY(0); } }
        .slider-value { animation: value-pop 0.18s ease; }
        .main-grid {
          display: grid;
          grid-template-columns: 260px 1fr 340px;
          gap: 16px;
          max-width: 1568px;
          margin: 0 auto;
        }
        .dims-card      { grid-column: 1; grid-row: 1; }
        .props-card     { grid-column: 1; grid-row: 2; }
        .geo-card       { grid-column: 2; grid-row: 1 / span 2; }
        .mode-list-card { grid-column: 3; grid-row: 1 / span 2; }
        .metrics-card   { grid-column: 1; grid-row: 3; }
        .bottom-charts  { grid-column: 2 / -1; grid-row: 3; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .mobile-toggle-btn { display: none; }
        .spectrum-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .spectrum-title-row { display: contents; }
        .spectrum-title-text { flex: 1; min-width: 0; }
        .spectrum-filters { display: flex; gap: 8px; }
        .bottom-charts-inner { display: flex; align-items: flex-start; gap: 0; }
        .spectrum-panel { flex: 1; min-width: 0; }
        .pressure-panel { flex: 0 0 272px; border-left: 1px solid var(--c-border); margin-left: 18px; padding-left: 18px; }
        @media (max-width: 1200px) {
          .main-grid { grid-template-columns: 240px 1fr 300px; gap: 12px; }
          .pressure-panel { flex: 0 0 240px; }
        }
        @media (max-width: 960px) {
          .main-grid { grid-template-columns: 220px 1fr; gap: 12px; }
          .mode-list-card { grid-column: 1 / -1; grid-row: 3; height: 300px; }
          .metrics-card   { grid-column: 1; grid-row: 4; }
          .bottom-charts  { grid-column: 1 / -1; grid-row: 5; }
          .bottom-charts-inner { flex-direction: column; align-items: stretch; }
          .pressure-panel { flex: none; width: 100%; border-left: none; margin-left: 0; padding-left: 0; border-top: 1px solid var(--c-border); margin-top: 16px; padding-top: 16px; }
        }
        @media (max-width: 600px) {
          .app-root { padding: 14px 16px; overflow-x: clip; }
          .main-grid { grid-template-columns: 1fr; gap: 10px; grid-auto-flow: row; }
          .geo-card       { order: 1; height: min(300px, 70vw); grid-column: 1; grid-row: auto; position: sticky !important; top: 28px; z-index: 10; }
          .geo-card canvas { width: 100% !important; height: 100% !important; }
          .dims-card      { order: 2; grid-column: 1; grid-row: auto; }
          .mode-list-card { order: 3; grid-column: 1; grid-row: auto; height: 450px; }
          .props-card     { order: 4; grid-column: 1; grid-row: auto; }
          .metrics-card   { order: 5; grid-column: 1; grid-row: auto; }
          .bottom-charts  { order: 6; grid-column: 1; grid-row: auto; }
          .unit-btn { min-height: 44px !important; padding: 8px 14px !important; }
          .type-filter-btn { padding: 10px 12px !important; }
          .mode-data-cell { padding-top: 9px !important; padding-bottom: 9px !important; }
          .mobile-toggle-btn { display: flex; align-items: center; justify-content: center; background: var(--c-surface); border: 1px solid var(--c-border-2); border-radius: 6px; color: var(--c-text-2); cursor: pointer; font-size: 18px; font-weight: 300; min-width: 44px; min-height: 44px; padding: 0; flex-shrink: 0; font-family: sans-serif; }
          .collapsible { display: none; }
          .collapsible.open { display: block; margin-top: 10px; }
          .site-header { text-align: center; padding-bottom: 8px; } /* Adds space beneath the Sebacoustics and Room Mode Calculator header text */
          .header-subtitle { display: none; }
          .spectrum-header { align-items: stretch; gap: 8px; margin-bottom: 0; }
          .spectrum-title-row { display: flex; width: 100%; align-items: center; gap: 10px; }
          .spectrum-title-text { display: flex; align-items: center; min-height: 44px; }
          .spectrum-title-row .mobile-toggle-btn { margin-left: auto; align-self: center; }
          .spectrum-filters { flex-wrap: wrap; gap: 6px; justify-content: center; width: 100%; }
        }
      `}</style>

      {/* Header */}
      <div className="site-header" style={{ maxWidth: 1568, margin: "0 auto", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, right: 0, display: "flex", gap: 8 }}>
          {/* Share button — disabled
          <button
            className="theme-toggle"
            onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            title="Copy shareable link"
            style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, letterSpacing: "0.04em", minWidth: 80 }}
          >
            {copied ? "✓ Copied" : "Share"}
          </button>
          */}
          <button
            className="theme-toggle"
            onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
        </div>
        <div style={{ marginBottom: 2 }}> {/* margin below sebacoustics, was previously 4px */}
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", color: "var(--c-text-2)", textTransform: "uppercase" }}>
            Sebacoustics
          </span>
        </div>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 2px 0", letterSpacing: "-0.02em" }}>
          Room Mode Calculator
        </h1>
        <p className="header-subtitle" style={{ color: "var(--c-text-3)", fontSize: 12, margin: "0 0 20px 0", fontWeight: 400 }}>
          Rectangular room eigenfrequency analysis &mdash; rigid wall boundary conditions
        </p>

        {/* Tab bar — hidden until Structural Modal Analysis is ready for release */}
        {/* CHUNK_1_HIDDEN: uncomment to enable tab navigation
        <div className="tab-bar" style={{ display: 'flex', gap: 0, marginBottom: 16, background: 'rgba(138,143,152,0.06)', borderRadius: 8, padding: 3, maxWidth: 380 }}>
          {[['acoustic', 'Acoustic Modes'], ['structural', 'Structural Modal']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                fontWeight: activeTab === key ? 700 : 500,
                padding: '7px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: activeTab === key ? 'var(--c-card)' : 'transparent',
                color: activeTab === key ? '#E8C547' : 'var(--c-text-3)',
                boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
                transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
                letterSpacing: '0.02em',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        */}
      </div>

      {/* Main layout — 3 columns: [Dims+Props] [Geometry — dominant] [Mode List] */}
      {/* maxWidth 1568 = 960 + 2×304 ensures Room Geometry left edge aligns with title text */}
      <div className="main-grid">

        {/* Left column: Room Dimensions + Room Properties */}
        <div className="app-card dims-card" style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--c-text-3)" }}>
                Room Dimensions
              </span>
              <div style={{ display: "flex", background: "rgba(138,143,152,0.08)", borderRadius: 6, padding: 2, gap: 2 }}>
                {["metric", "imperial"].map(u => (
                  <button key={u} className="unit-btn" onClick={() => setUnits(u)} style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600,
                    padding: "3px 8px", borderRadius: 4, border: "none", cursor: "pointer",
                    background: units === u ? "rgba(138,143,152,0.2)" : "transparent",
                    color: units === u ? "var(--c-text)" : "var(--c-text-3)",
                    transition: "background 0.15s, color 0.15s",
                  }}>{u === "metric" ? "m" : "ft"}</button>
                ))}
              </div>
            </div>
            <Slider label="Length (x)" value={Lx} onChange={setLx} min={2} max={20} step={0.1} unit="m" imperial={imperial} onInteractStart={onDimInteractStart} onInteractEnd={onDimInteractEnd} />
            <Slider label="Width (y)" value={Ly} onChange={setLy} min={2} max={15} step={0.1} unit="m" imperial={imperial} onInteractStart={onDimInteractStart} onInteractEnd={onDimInteractEnd} />
            <Slider label="Height (z)" value={Lz} onChange={setLz} min={2} max={8} step={0.1} unit="m" imperial={imperial} onInteractStart={onDimInteractStart} onInteractEnd={onDimInteractEnd} />
            <div className="max-freq-control" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(138,143,152,0.1)" }}>
              <Slider label="Max Frequency" value={maxFreq} onChange={setMaxFreq} min={100} max={500} step={10} unit="Hz" />
            </div>
          </div>

        <div className="app-card props-card" style={{ ...cardStyle, padding: 12, marginBottom: 0 }}>
            <div className="section-header">
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--c-text-3)" }}>
                Room Properties
              </span>
              <button className="mobile-toggle-btn" onClick={() => setShowProps(v => !v)}>
                {showProps ? "−" : "+"}
              </button>
            </div>
            <div className={`collapsible${showProps ? " open" : ""}`}>
            <div style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", lineHeight: 1.8 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--c-text-2)" }}>Volume</span>
                <span key={imperial ? 'i' : 'm'} className="slider-value" style={{ color: "var(--c-text)" }}>{imperial ? `${(V * 35.3147).toFixed(1)} ft³` : `${V.toFixed(1)} m³`}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--c-text-2)" }}>Surface Area</span>
                <span key={imperial ? 'i' : 'm'} className="slider-value" style={{ color: "var(--c-text)" }}>{imperial ? `${(S * 10.7639).toFixed(1)} ft²` : `${S.toFixed(1)} m²`}</span>
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "var(--c-text-2)" }}>L : W : H</span>
                  <span style={{ color: "var(--c-text)" }}>{ratioLH} : {ratioWH} : 1</span>
                </div>
                <div style={{ display: "flex", gap: 2, height: 3, borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ flex: Lx, background: "rgba(232,197,71,0.5)", borderRadius: 2 }} />
                  <div style={{ flex: Ly, background: "rgba(78,205,196,0.4)", borderRadius: 2 }} />
                  <div style={{ flex: Lz, background: "rgba(149,224,108,0.4)", borderRadius: 2 }} />
                </div>
              </div>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(138,143,152,0.08)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--c-text-2)" }}>Total Modes</span>
                <span style={{ color: "var(--c-text)", fontWeight: 600 }}>{modes.length}</span>
              </div>
              {[["axial", axialCount], ["tangential", tangentialCount], ["oblique", obliqueCount]].map(([type, count]) => (
                <div key={type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--c-text-2)" }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: typeColors[type], flexShrink: 0, display: "inline-block" }} />
                    {typeLabels[type]}
                  </span>
                  <span style={{ color: "var(--c-text)" }}>{count}</span>
                </div>
              ))}
            </div>
            </div>{/* end collapsible */}
          </div>

        {/* Center column: Room Geometry — position:relative so grid row = left column height */}
        <div className="app-card geo-card" style={{
          background: "var(--c-card)", borderRadius: 12, border: "1px solid rgba(138,143,152,0.08)",
          marginBottom: 0, overflow: "hidden", position: "relative",
        }}>
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
            display: "flex", flexDirection: "column", padding: "20px 20px 16px",
          }}>
            <span style={sectionLabel}>Room Geometry</span>
            {USE_GL_RENDERER
              ? <RoomVizGL Lx={Lx} Ly={Ly} Lz={Lz} mode={selectedMode} imperial={imperial} theme={theme} dimInteracting={dimInteracting} activeColormap={activeColormap} setActiveColormap={setActiveColormap} lut={lut} animPlaying={animPlaying} onPhaseUpdate={handleGlPhaseUpdate} phaseResetTrigger={phaseResetCount} />
              : <RoomViz Lx={Lx} Ly={Ly} Lz={Lz} mode={selectedMode} imperial={imperial} theme={theme} dimInteracting={dimInteracting} activeColormap={activeColormap} setActiveColormap={setActiveColormap} lut={lut} animPlaying={animPlaying} />}
          </div>
        </div>

        {/* Right column: Mode List — absolute interior so card height = 0 for grid sizing,
            grid row height comes from Room Geometry only, card stretches via align-self:stretch */}
        <div className="app-card mode-list-card" style={{
          background: "var(--c-card)", borderRadius: 12, border: "1px solid rgba(138,143,152,0.08)",
          marginBottom: 0, overflow: "hidden", position: "relative", minHeight: 200,
        }}>
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
            display: "flex", flexDirection: "column", padding: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
              <span style={{ ...sectionLabel, marginBottom: 0 }}>Mode List</span>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {Object.entries(typeLabels).map(([key, label]) => {
                  const on = visibleTypes[key];
                  return (
                    <button
                      key={key}
                      className="type-filter-btn"
                      aria-pressed={on}
                      onClick={() => setVisibleTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{
                        display: "flex", alignItems: "center", gap: 5,
                        background: on ? "rgba(255,255,255,0.04)" : "transparent",
                        border: `1px solid ${on ? "rgba(138,143,152,0.2)" : "rgba(138,143,152,0.08)"}`,
                        borderRadius: 5, padding: "3px 8px", cursor: "pointer",
                        opacity: on ? 1 : 0.38, transition: "opacity 0.15s, border-color 0.15s",
                      }}
                    >
                      <div style={{ width: 7, height: 7, borderRadius: 2, background: on ? typeColors[key] : "var(--c-text-3)", flexShrink: 0 }} />
                      <span style={{ fontSize: 10, color: on ? "var(--c-text-2)" : "var(--c-text-3)", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedMode ? (() => {
              const live = modes.find(m => m.nx === selectedMode.nx && m.ny === selectedMode.ny && m.nz === selectedMode.nz);
              return live ? (
                <div style={{ marginBottom: 10, padding: '10px 12px', background: 'rgba(232,197,71,0.06)', border: '1px solid rgba(232,197,71,0.18)', borderRadius: 8, flexShrink: 0, maxHeight: 120, overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0, overflow: 'hidden' }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 600, color: '#E8C547', whiteSpace: 'nowrap' }}>
                        ({live.nx},{live.ny},{live.nz})
                      </span>
                      <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: typeColors[live.type], whiteSpace: 'nowrap' }}>
                        {typeLabels[live.type]}
                      </span>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--c-text)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {live.freq.toFixed(1)} Hz
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      <button
                        onClick={() => { setAnimPlaying(v => !v); setShowResetButton(true); }}
                        aria-label={animPlaying ? 'Pause animation' : 'Animate mode shape'}
                        title={animPlaying ? 'Pause' : 'Animate mode shape'}
                        style={{
                          background: animPlaying ? 'rgba(232,197,71,0.12)' : 'none',
                          border: `1px solid ${animPlaying ? 'rgba(232,197,71,0.4)' : 'var(--c-border-2)'}`,
                          color: animPlaying ? '#E8C547' : 'var(--c-text-2)',
                          borderRadius: 4, width: 26, height: 26, padding: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', lineHeight: 1,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          transition: 'background 0.12s, border-color 0.12s, color 0.12s',
                        }}
                      >
                        {animPlaying
                          ? <span style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: 'translateY(-1px)' }}>⏸</span>
                          : <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: 'translateX(1px)' }}>▶</span>
                        }
                      </button>
                      <button
                        onClick={showResetButton ? handlePhaseReset : () => setSelectedMode(null)}
                        aria-label={showResetButton ? 'Reset animation phase' : 'Close window'}
                        title={showResetButton ? 'Reset animation' : 'Close window'}
                        style={{
                          background: 'none',
                          border: `1px solid var(--c-border-2)`,
                          color: 'var(--c-text-2)',
                          borderRadius: 4, width: 26, height: 26, padding: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', lineHeight: 1,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          transition: 'color 0.12s',
                        }}
                      >
                        {/* Custom filled-ring SVG icon (commented out — using ↺ instead)
                        {showResetButton ? (
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                            <path d="M1.7 15.8A11 11 0 1 0 6.5 2.5L1.6 7.9L8.75 6.4A6.5 6.5 0 1 1 5.9 14.2Z"/>
                          </svg>
                        ) : '✕'}
                        */}
                        {showResetButton ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 16, height: 16, fontSize: 18, lineHeight: 1,
                            transform: 'translate(-1.5px, -1.5px) rotate(-45deg)',
                          }}>↺</span>
                        ) : (
                          <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: 'translateY(-0.7px)' }}>✕</span> /* X button */
                        )}
                      </button>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: 'var(--c-text-2)', lineHeight: 1.5 }}>
                    {modeDescription(live)}
                  </p>
                </div>
              ) : null;
            })() : (
              <div style={{ marginBottom: 8, padding: '7px 10px', background: 'var(--c-surface)', borderRadius: 6, flexShrink: 0 }}>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: 'var(--c-text-3)' }}>
                  Click any mode in the list or spectrum to analyze it
                </span>
              </div>
            )}
            <ModeTable modes={filteredModes} selectedMode={selectedMode} onSelectMode={setSelectedMode} />
          </div>
        </div>

        {/* Acoustic Metrics — col 1 of bottom row */}
        <div className="app-card metrics-card" style={{ ...cardStyle, marginBottom: 0 }}>
          <div className="section-header">
            <span style={{ ...sectionLabel, display: "inline", marginBottom: 0 }}>Acoustic Metrics</span>
            <button className="mobile-toggle-btn" onClick={() => setShowMetrics(v => !v)}>
              {showMetrics ? "−" : "+"}
            </button>
          </div>
          <div className={`collapsible${showMetrics ? " open" : ""}`}>
          <Slider label="Est. RT60" value={rt60} onChange={setRt60} min={0.1} max={2.0} step={0.05} unit="s" />
          <div style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", lineHeight: 1.9, marginTop: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "var(--c-text-2)", display: 'flex', alignItems: 'center' }}>
                Schroeder Freq
                <InfoTooltip text="Below this frequency, room modes dominate. Above it, sound becomes more diffuse. Lower your RT60 or enlarge the room to push this number down." />
              </span>
              <span style={{ color: "var(--c-text)" }}>~{acousticMetrics.schroeder} Hz</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--c-text-2)" }}>Modal Bandwidth</span>
              <span style={{ color: "var(--c-text)" }}>{acousticMetrics.bandwidth.toFixed(1)} Hz</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--c-text-2)" }}>Avg Mode Spacing</span>
              <span style={{ color: "var(--c-text)" }}>{acousticMetrics.avgSpacing.toFixed(1)} Hz</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "var(--c-text-2)", display: 'flex', alignItems: 'center' }}>
                Modal Overlap
                <InfoTooltip text="Modes overlapping per Hz band. Below 1: distinct resonances, audible bass coloration. Above 3: smooth diffuse field. Aim for >1 for flatter low-end response." />
              </span>
              <span>
                <span style={{ color: acousticMetrics.mof < 1 ? "#F59E0B" : acousticMetrics.mof >= 3 ? "#95E06C" : "var(--c-text)" }}>
                  {acousticMetrics.mof.toFixed(2)}
                </span>
                <span style={{
                  color: acousticMetrics.mof < 1 ? "#F59E0B" : acousticMetrics.mof >= 3 ? "#95E06C" : "var(--c-text-2)",
                  background: acousticMetrics.mof < 1 ? "rgba(245,158,11,0.1)" : acousticMetrics.mof >= 3 ? "rgba(149,224,108,0.1)" : "rgba(138,143,152,0.08)",
                  fontSize: 9, marginLeft: 5, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                  padding: "2px 5px", borderRadius: 3,
                }}>
                  {acousticMetrics.mof < 1 ? "Modal" : acousticMetrics.mof >= 3 ? "Diffuse" : "Transitional"}
                </span>
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "var(--c-text-2)", display: 'flex', alignItems: 'center' }}>
                Critical Distance
                <InfoTooltip text="Distance from a speaker where direct and reverberant sound are equal. Beyond this, the room dominates. Useful for speaker placement and mic positioning." />
              </span>
              <span key={imperial ? 'i' : 'm'} className="slider-value" style={{ color: "var(--c-text)" }}>{imperial ? formatFtInShort(acousticMetrics.critDist) : `${acousticMetrics.critDist.toFixed(2)} m`}</span>
            </div>
          </div>
          {acousticMetrics.topGaps.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(138,143,152,0.08)" }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--c-text-3)", marginBottom: 6 }}>
                Largest Gaps
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, lineHeight: 1.9 }}>
                {acousticMetrics.topGaps.map((g, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ color: "var(--c-text-2)" }}>{Math.round(g.f1)}–{Math.round(g.f2)} Hz</span>
                    <span style={{ color: "#E8C547", fontWeight: 500 }}>+{g.gap.toFixed(1)} Hz</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>{/* end collapsible */}
        </div>

        {/* Mode Spectrum + Pressure Field — cols 2-3 of bottom row */}
        <div className="app-card bottom-charts" style={{ ...cardStyle, marginBottom: 0 }}>
          <div className="bottom-charts-inner">

            {/* Left: spectrum header + collapsible spectrum/density */}
            <div className="spectrum-panel">
              <div className="spectrum-header">
                <div className="spectrum-title-row">
                  <span className="spectrum-title-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--c-text-3)" }}>
                    Mode Spectrum{!selectedMode
                      ? <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none" }}> — click a line to select a mode</span>
                      : (() => { const live = modes.find(m => m.nx === selectedMode.nx && m.ny === selectedMode.ny && m.nz === selectedMode.nz); return live ? <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none", color: typeColors[live.type] }}> — ({live.nx}, {live.ny}, {live.nz}) {typeLabels[live.type]} · {live.freq.toFixed(1)} Hz</span> : null; })()
                    }
                  </span>
                  <button className="mobile-toggle-btn" onClick={() => setShowCharts(v => !v)}>
                    {showCharts ? "−" : "+"}
                  </button>
                </div>
              </div>
              <div className={`collapsible${showCharts ? " open" : ""}`}>
                <ModeSpectrum modes={filteredModes} modesByType={filteredModesByType} maxFreq={maxFreq} selectedMode={selectedMode} onSelectMode={setSelectedMode} />
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(138,143,152,0.08)" }}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--c-text-3)", marginBottom: 6 }}>
                    Mode Density <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none" }}>— per 10 Hz band</span>
                  </div>
                  <DensityBars modes={modes} maxFreq={maxFreq} />
                </div>
              </div>
            </div>

            {/* Right: pressure field slice — only shown when a mode is selected */}
            {selectedMode && (() => {
              const live = modes.find(m => m.nx === selectedMode.nx && m.ny === selectedMode.ny && m.nz === selectedMode.nz);
              return live ? (
                <div className="pressure-panel">
                  <PressureHeatmap
                    mode={live}
                    Lx={Lx} Ly={Ly} Lz={Lz}
                    lut={lut}
                    activeColormap={activeColormap}
                    imperial={imperial}
                    animPlaying={animPlaying}
                    glPhaseRef={glPhaseRef}
                    phaseResetTrigger={phaseResetCount}
                  />
                </div>
              ) : null;
            })()}

          </div>
        </div>
      </div>

      {/* Structural Modal Analysis tab — hidden until ready for release */}
      {/* CHUNK_1_HIDDEN: uncomment to enable structural tab
      {activeTab === 'structural' && <StructuralModal />}
      */}

      {/* Footer */}
      <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid rgba(138,143,152,0.06)", textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--c-text-3)" }}>
        f(nₓ, nᵧ, n_z) = (c/2)√((nₓ/Lₓ)² + (nᵧ/Lᵧ)² + (n_z/L_z)²) &nbsp;|&nbsp; c = {SPEED_OF_SOUND} m/s &nbsp;|&nbsp; Rigid wall BCs
      </div>
    </div>
  );
}
